import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Card, Combobox, Input, Spinner } from '@/components/common'
import { Button } from '@/components/common/Button'
import { FeedbackSheet } from '@/components/features/feedback/FeedbackSheet'
import { FeedbackList } from '@/components/features/feedback/FeedbackList'
import {
  useFeedbackList,
  useFeedbackSubmitters,
  useSetFeedbackStatus,
  useSetFeedbackType,
  type FeedbackStatus,
} from '@/hooks/useFeedback'
import { useDebouncedValue } from '@/hooks/useDebouncedValue'
import { useHasPermission } from '@/hooks/usePermissions'
import { useAuth } from '@/providers/AuthProvider'
import styles from '@/components/features/feedback/FeedbackPage.module.css'

export const Route = createFileRoute('/_authenticated/feedback')({
  // `?focus=<id>` deep-links from a notification to a specific ticket (#920).
  validateSearch: (s: Record<string, unknown>): { focus?: string } => ({
    focus: typeof s.focus === 'string' ? s.focus : undefined,
  }),
  component: FeedbackPage,
})

function FeedbackPage() {
  const { focus } = Route.useSearch()
  const navigate = useNavigate()
  // Consume the notification `focus` once, then strip it from the URL so a
  // reload / re-navigation to the listing never re-opens comments (#920).
  const [activeFocus, setActiveFocus] = useState<string | undefined>(focus)
  useEffect(() => {
    if (!focus) return
    setActiveFocus(focus)
    void navigate({ to: '/feedback', search: {}, replace: true })
  }, [focus, navigate])

  const [isFormOpen, setFormOpen] = useState(false)
  const [submitterId, setSubmitterId] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const search = useDebouncedValue(searchInput.trim(), 300)
  // Default to the active view — hides closed tickets (#918). When deep-linking
  // to a specific ticket, start on "All" so a closed target is still found (#920).
  const [status, setStatus] = useState<FeedbackStatus | 'active' | 'all'>(focus ? 'all' : 'active')

  const { user } = useAuth()
  const canManageStatus = useHasPermission('feedback', 'manage_status')
  const setFeedbackStatus = useSetFeedbackStatus()
  const setFeedbackType = useSetFeedbackType()

  const { data: submitters = [] } = useFeedbackSubmitters()

  const {
    data,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useFeedbackList({ submitterId, search, status })

  const rows = useMemo(() => data?.pages.flatMap((p) => p.rows) ?? [], [data])
  const total = data?.pages[0]?.total ?? 0

  const submitterOptions = useMemo(
    () => [{ value: '', label: 'All people' }, ...submitters.map((s) => ({ value: s.id, label: s.name }))],
    [submitters],
  )
  const statusOptions = useMemo(
    () => [
      { value: 'active', label: 'Active' },
      { value: 'closed', label: 'Closed' },
      { value: 'all', label: 'All' },
    ],
    [],
  )
  // Only worth showing when the caller can see more than one person's feedback.
  const showSubmitterFilter = submitters.length > 1

  // Infinite scroll: observe a sentinel below the list and load the next page
  // when it enters view. Callback refs must not return a value, so keep the
  // observer in a ref and reconnect on each call.
  const observerRef = useRef<IntersectionObserver | null>(null)
  const onSentinel = useCallback(
    (node: HTMLDivElement | null) => {
      observerRef.current?.disconnect()
      if (!node || !hasNextPage) return
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
            fetchNextPage()
          }
        },
        { rootMargin: '300px' },
      )
      observer.observe(node)
      observerRef.current = observer
    },
    [hasNextPage, isFetchingNextPage, fetchNextPage],
  )

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.headerRow}>
          <div>
            <h1 className={styles.title}>Feedback</h1>
            <p className={styles.subtitle}>
              {total} {total === 1 ? 'submission' : 'submissions'}
            </p>
          </div>
          <Button className={styles.addButton} onClick={() => setFormOpen(true)}>
            <svg
              className={styles.addIcon}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              aria-hidden
            >
              <path d="M12 5v14M5 12h14" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Add Feedback
          </Button>
        </div>

        <div className={styles.filters}>
          <Input
            className={styles.search}
            placeholder="Search feedback or ZRM-FB-123…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            aria-label="Search feedback text"
          />
          <div className={styles.statusFilter}>
            <Combobox
              value={status}
              onChange={(v) => setStatus(v as FeedbackStatus | 'active' | 'all')}
              options={statusOptions}
              placeholder="All statuses"
            />
          </div>
          {showSubmitterFilter && (
            <div className={styles.submitterFilter}>
              <Combobox
                value={submitterId}
                onChange={setSubmitterId}
                options={submitterOptions}
                placeholder="All people"
              />
            </div>
          )}
        </div>
      </div>

      <div className={styles.body}>
        <Card padding="none">
          <FeedbackList
            items={rows}
            isLoading={isLoading}
            isError={isError}
            canManageStatus={canManageStatus}
            currentUserId={user?.id}
            onStatusChange={(id, s, note) => setFeedbackStatus.mutate({ id, status: s, note })}
            onTypeChange={(id, t) => setFeedbackType.mutate({ id, type: t })}
            focusId={activeFocus}
          />
        </Card>

        {hasNextPage && <div ref={onSentinel} className={styles.sentinel} aria-hidden />}
        {isFetchingNextPage && (
          <div className={styles.loadingMore}>
            <Spinner />
          </div>
        )}
      </div>

      <FeedbackSheet isOpen={isFormOpen} onClose={() => setFormOpen(false)} />
    </div>
  )
}
